"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HarmonyAdapter = void 0;
const core_1 = require("@harmony-js/core");
const crypto_1 = require("@harmony-js/crypto");
const utils_1 = require("@harmony-js/utils");
const ERC20_1 = require("../../../Abis/ERC20");
const Types_1 = require("../../../Types");
const BaseAdapter_1 = require("../../../Adapters/BaseAdapter");
const Helpers_1 = require("../../../Adapters/Helpers");
const utils_2 = require("ethers/lib/utils");
const utils_3 = require("@unifiprotocol/utils");
const Errors_1 = require("../../../Errors");
class HarmonyAdapter extends BaseAdapter_1.BaseAdapter {
    constructor() {
        super(...arguments);
        this.contracts = {};
        this.stablePairs = [];
        this.lastGasLimit = "30000";
        this.abi = {};
        this.harmonyClient = new core_1.Harmony(`https://api.s0.t.hmny.io`, {
            chainType: utils_1.ChainType.Harmony,
            chainId: 1,
        });
    }
    signMessage() {
        throw new Errors_1.NotImplementedError();
    }
    convertAddressTo(address, format) {
        if (format === Types_1.AddressFormat.Native) {
            return address.startsWith("one") ? address : crypto_1.toBech32(address);
        }
        return address.startsWith("0x") ? address : crypto_1.fromBech32(address);
    }
    setProvider(provider) {
        this._provider = provider;
    }
    getProvider() {
        return this._provider;
    }
    resetContracts() {
        this.contracts = {};
    }
    isConnected() {
        return !!this.address;
    }
    setAddress(address) {
        this.address = new crypto_1.HarmonyAddress(address).basicHex;
    }
    get hexAddress() {
        return new crypto_1.HarmonyAddress(this.address).basicHex;
    }
    get oneAddress() {
        return new crypto_1.HarmonyAddress(this.address).bech32;
    }
    initializeContract(contractAddress, abi) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.contracts[contractAddress] ||
                contractAddress === this.blockchainConfig.nativeToken.address) {
                return;
            }
            const contract = this.harmonyClient.contracts.createContract(abi, this.toHexAddress(contractAddress));
            contract.wallet.signTransaction = this._provider.signTransaction;
            this.contracts[this.toHexAddress(contractAddress)] = contract;
        });
    }
    prepareExecuteArgs(args) {
        return args.map((v) => {
            if (typeof v === "string")
                return v;
            if (Array.isArray(v)) {
                return this.prepareExecuteArgs(v);
            }
            return utils_2.hexlify(v);
        });
    }
    execute(contractAddress, method, values, isWrite) {
        return __awaiter(this, void 0, void 0, function* () {
            contractAddress = this.toHexAddress(contractAddress);
            if (contractAddress === this.blockchainConfig.nativeToken.address &&
                ["allowance", "approve"].includes(method)) {
                return Helpers_1.successResponse({
                    method,
                    value: utils_3.BN(Math.pow(2, 256)).toFixed(),
                });
            }
            try {
                const contractArgs = this.prepareExecuteArgs(values.args);
                const contractMethod = this.contracts[contractAddress].methods[method](...contractArgs);
                const txOpts = { gasPrice: "0x3B9ACA00", from: this.hexAddress };
                if (values.callValue) {
                    txOpts.value = values.callValue;
                }
                const estimatedGasLimit = yield contractMethod
                    .estimateGas(txOpts)
                    .then(utils_1.hexToNumber);
                const opts = Object.assign(Object.assign({}, txOpts), { gasLimit: estimatedGasLimit });
                let res;
                if (isWrite) {
                    const { transaction } = yield contractMethod.send(opts);
                    res = {
                        hash: transaction.id,
                    };
                }
                else {
                    const value = yield contractMethod.call(opts);
                    res = {
                        value,
                    };
                }
                return Helpers_1.successResponse(Object.assign(Object.assign({ method }, res), { value: decodeValue(res.value) }));
            }
            catch (err) {
                return Helpers_1.nonSuccessResponse({
                    method,
                    err,
                });
            }
        });
    }
    waitForTransaction(txnHash) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                const checkTx = () => {
                    return this.harmonyClient.blockchain
                        .getTransactionReceipt({
                        txnHash,
                    })
                        .then((res) => {
                        var _a;
                        const status = {
                            "0x0": Types_1.TransactionStatus.Failed,
                            "0x1": Types_1.TransactionStatus.Success,
                        }[(_a = res === null || res === void 0 ? void 0 : res.result) === null || _a === void 0 ? void 0 : _a.status] || undefined;
                        if (status) {
                            resolve(status);
                        }
                        else {
                            setTimeout(checkTx, 1500);
                        }
                    });
                };
                checkTx();
            });
        });
    }
    getContractInterface(contractAddress) {
        return this.contracts[this.toHexAddress(contractAddress)].abi;
    }
    getBalance(address = this.address) {
        return __awaiter(this, void 0, void 0, function* () {
            const hexBalance = yield this.harmonyClient.blockchain.getBalance({
                address,
            });
            const balance = this.harmonyClient.utils.hexToNumber(hexBalance.result);
            return {
                name: this.blockchainConfig.nativeToken.symbol,
                balance,
            };
        });
    }
    isValidNetwork(network) {
        return __awaiter(this, void 0, void 0, function* () {
            return network === `1`;
        });
    }
    isValidAddress(address) {
        return this.harmonyClient.utils.isValidAddress(address);
    }
    getAddressLink(address) {
        return this.blockchainConfig.explorer.address(address);
    }
    getTokenLink(address) {
        return this.blockchainConfig.explorer.token(address);
    }
    getTxLink(hash) {
        return this.blockchainConfig.explorer.tx(`${hash}`);
    }
    getDecodedTransactionWithLogs() {
        throw new Error("Method not implemented. Use Harmony Web3 Adapter ");
    }
    getTransactionsFromEvents() {
        throw new Error("Method not implemented. Use Harmony Web3 Adapter ");
    }
    initializeToken(tokenAddress, abi = ERC20_1.ERC20ABI) {
        return __awaiter(this, void 0, void 0, function* () {
            this.initializeContract(tokenAddress, abi);
        });
    }
    toHexAddress(address) {
        if (this.blockchainConfig.nativeToken.address === address) {
            return address;
        }
        return new crypto_1.HarmonyAddress(address).basicHex;
    }
    getBlock(height) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getHarmonyBlock(height).then(mapHmyBlockToGlobal);
        });
    }
    getHarmonyBlock(height) {
        return __awaiter(this, void 0, void 0, function* () {
            height = this.sanitizeBlock(height);
            if (height === "latest") {
                height = yield this.harmonyClient.blockchain
                    .getBlockNumber()
                    .then((response) => utils_1.hexToNumber(response.result));
            }
            if (utils_3.BN(height).isNaN()) {
                return this.harmonyClient.blockchain.getBlockByHash({
                    blockHash: `${height}`,
                });
            }
            else {
                return this.harmonyClient.blockchain.getBlockByNumber({
                    blockNumber: utils_1.numberToHex(height),
                });
            }
        });
    }
    getBlockWithTxs(height) {
        return __awaiter(this, void 0, void 0, function* () {
            const hmyBlockResult = yield this.getHarmonyBlock(height);
            const block = mapHmyBlockToGlobal(hmyBlockResult);
            block.transactions = hmyBlockResult.result.transactions.map((tx) => mapHmyTxToGlobal(block, tx));
            return block;
        });
    }
}
exports.HarmonyAdapter = HarmonyAdapter;
function decodeValue(value) {
    var _a;
    if (Array.isArray(value)) {
        return value.map((v) => decodeValue(v)).join(",");
    }
    if (((_a = value === null || value === void 0 ? void 0 : value.constructor) === null || _a === void 0 ? void 0 : _a.name) === "BN") {
        return value.toString();
    }
    return value;
}
function mapHmyTxToGlobal(block, hmyTx) {
    return {
        hash: hmyTx.hash,
        value: utils_1.hexToNumber(hmyTx.value),
        txFee: null,
        blockNumber: block.number,
        blockHash: block.hash,
        timestamp: block.timestamp,
        from: hmyTx.from,
        to: hmyTx.to,
        raw: hmyTx.input,
    };
}
function mapHmyBlockToGlobal({ result }) {
    return {
        hash: result.hash,
        parentHash: result.parentHash,
        number: Number(utils_1.hexToNumber(result.number)),
        timestamp: Number(utils_1.hexToNumber(result.timestamp)),
        transactions: result.transactions.map((tx) => tx.hash),
    };
}
